import {EmbedCodeExtractor} from "./types"

/**
 * TikTok embed-code extractor (resolve-long-embed-codes, design D2). Sample embed code:
 *
 *   <blockquote class="tiktok-embed" cite="https://www.tiktok.com/@user/video/7664249441729187092"
 *     data-video-id="7664249441729187092" style="max-width: 605px;min-width: 325px;">
 *     <section> <a target="_blank" title="@user" href="https://www.tiktok.com/@user?refer=embed">@user</a> …
 *   </section> </blockquote> <script async src="https://www.tiktok.com/embed.js"></script>
 *
 * The canonical `@user/video/<id>` URL is taken from the `cite` attribute, falling back
 * to any inner anchor whose href is a video URL. `data-video-id` alone is deliberately
 * NOT used — it lacks the user handle, so the canonical URL cannot be reconstructed
 * from it. Without this extractor the TikTok provider's greedy URL pattern
 * (`tiktok\.com\/.*\/video\/.*`) mangles the surrounding blockquote markup.
 */
const TIKTOK_EMBED_MARKER = /class=["']tiktok-embed["']/i

// The blockquote tag carrying the tiktok-embed class — cite is searched within the
// matched tag so attribute order does not matter (`<blockquote cite=… class=…>` is
// just as valid as the official order; Copilot finding on PR #789).
const TIKTOK_BLOCKQUOTE_TAG = /<blockquote\b[^>]*class=["']tiktok-embed["'][^>]*>/i

const CITE_PATTERN = /\scite=["'](https?:\/\/(?:www\.|m\.)?tiktok\.com\/[^"']*\/video\/[^"']+)["']/i

const SECTION_PATTERN = /<section\b[^>]*>([\s\S]*?)<\/section>/i

const ANCHOR_PATTERN = /<a[^>]*\shref=["'](https?:\/\/(?:www\.|m\.)?tiktok\.com\/[^"']*\/video\/[^"']+)["']/i

/** Decode the HTML entities that can appear in an attribute-embedded URL. */
function decodeEntities(url: string): string {
  return url.replaceAll("&amp;", "&")
}

export const tiktokPreProcessor: EmbedCodeExtractor = (input: string) => {
  if(!TIKTOK_EMBED_MARKER.test(input)) return null
  const tag = TIKTOK_BLOCKQUOTE_TAG.exec(input)
  if(tag === null) return null
  const cite = CITE_PATTERN.exec(tag[0])
  if(cite) return decodeEntities(cite[1])
  // Fallback is scoped to THIS blockquote's inner <section> — searching the whole
  // input could pick up an unrelated TikTok video link next to the embed and resolve
  // the wrong post (Copilot finding on PR #789).
  const bodyStart = tag.index + tag[0].length
  const bodyEnd = input.indexOf("</blockquote>", bodyStart)
  const body = bodyEnd === -1 ? input.slice(bodyStart) : input.slice(bodyStart, bodyEnd)
  const section = SECTION_PATTERN.exec(body)
  if(section === null) return null
  const anchor = ANCHOR_PATTERN.exec(section[1])
  return anchor ? decodeEntities(anchor[1]) : null
}
